// Monitoring tab's chat + context-selection layout: left is the datatalk/RAG chat
// (LayerChatPanel), right is a grid of data cards where the professional picks which
// metrics/views (ContextOption: "14days" raw series or "trend") feed the LLM context —
// mirrors the ContextSelections shape formatMonitoringContext.ts expects on the backend.
import { useState } from "react"
import { ContextOption, ValueType } from "pokal_api"
import LayerChatPanel from "../chat/shared/LayerChatPanel"
import AnalyseModeToggle from "../shared/AnalyseModeToggle"
import ValueTypeOverviewComponent from "../../ValueTypeOverviewComponent"

type Props = {
    patientId: string
    onClose: () => void
}

type AnalyseMode = "monitoring" | "leitlinien"

const DATA_CARD_TYPES: { focused: ValueType; secondary?: ValueType }[] = [
    { focused: ValueType.PHQUESTIONNAIRE },
    { focused: ValueType.SFQUESTIONNAIRE },
    { focused: ValueType.MOOD },
    { focused: ValueType.STEPS },
    { focused: ValueType.SLEEP_DURATION, secondary: ValueType.SLEEP_LATENCY },
    { focused: ValueType.SLEEP_HEART_RATE, secondary: ValueType.SLEEP_HEART_RATE_VARIABILITY },
]

// Some data cards show two related metrics together (e.g. sleep duration + latency) but
// only the "focused" one has its own context-selection buttons. This copies the focused
// metric's selection onto its secondary so both are included in the LLM context together.
function withSecondaryMirrored(
    selections: Partial<Record<ValueType, ContextOption[]>>
): Partial<Record<ValueType, ContextOption[]>> {
    const result: Partial<Record<ValueType, ContextOption[]>> = { ...selections }
    for (const { focused, secondary } of DATA_CARD_TYPES) {
        if (secondary !== undefined) {
            const opts = selections[focused]
            if (opts && opts.length > 0) {
                result[secondary] = [...opts]
            }
        }
    }
    return result
}

export default function MonitoringAnalysisPanel({ patientId, onClose }: Props) {
    const [mode, setMode] = useState<AnalyseMode>("monitoring")
    const [selectedDocumentIds, setSelectedDocumentIds] = useState<string[]>([])
    const [similarityThreshold, setSimilarityThreshold] = useState(0.3)
    const [topK, setTopK] = useState(5)
    const [contextSelections, setContextSelections] = useState<Partial<Record<ValueType, ContextOption[]>>>(
        Object.fromEntries(DATA_CARD_TYPES.map(c => [c.focused, [] as ContextOption[]]))
    )

    function selectAll(option: ContextOption) {
        setContextSelections(
            Object.fromEntries(DATA_CARD_TYPES.map(({ focused }) => [
                focused,
                [...new Set([...(contextSelections[focused] ?? []), option])]
            ]))
        )
    }

    function clearAll() {
        setContextSelections(
            Object.fromEntries(DATA_CARD_TYPES.map(({ focused }) => [focused, [] as ContextOption[]]))
        )
    }

    return (
        <div className="grid grid-cols-2 gap-4">
            {/* LEFT: mode toggle + chat, absolute-filled so right column drives row height */}
            <div className="relative">
                <div className="absolute inset-0 flex flex-col overflow-hidden gap-2 pt-1">
                    <div className="flex-shrink-0">
                        <AnalyseModeToggle
                            mode={mode}
                            onModeChange={m => setMode(m as AnalyseMode)}
                            dataMode="monitoring"
                            dataModeLabel="nur Daten"
                        />
                    </div>
                    <div className="flex-1 min-h-0">
                        <LayerChatPanel
                            patientId={patientId}
                            mode={mode}
                            pageContext="monitoring"
                            onClose={onClose}
                            selectedDocumentIds={selectedDocumentIds}
                            onSelectionChange={setSelectedDocumentIds}
                            similarityThreshold={similarityThreshold}
                            onThresholdChange={setSimilarityThreshold}
                            topK={topK}
                            onTopKChange={setTopK}
                            contextSelections={withSecondaryMirrored(contextSelections)}
                        />
                    </div>
                </div>
            </div>

            {/* RIGHT: context selection + data cards — natural height drives the row */}
            <div className="flex flex-col gap-2">
                <div className="flex items-center justify-between px-1">
                    <span className="text-xs font-semibold text-gray-600">Möchten Sie alle Daten für die Analyse verwenden?</span>
                    <div className="flex gap-1">
                        <button
                            type="button"
                            onClick={() => selectAll("14days")}
                            className="text-xs bg-white border border-gray-300 rounded px-2 py-0.5 text-gray-600 hover:border-teal-400 hover:text-teal-600"
                        >
                            die letzten 14 Tage
                        </button>
                        <button
                            type="button"
                            onClick={() => selectAll("trend")}
                            className="text-xs bg-white border border-gray-300 rounded px-2 py-0.5 text-gray-600 hover:border-teal-400 hover:text-teal-600"
                        >
                            alle Trends
                        </button>
                        <button
                            type="button"
                            onClick={clearAll}
                            className="text-xs bg-white border border-gray-300 rounded px-2 py-0.5 text-gray-500 hover:border-red-400 hover:text-red-500"
                        >
                            ✕
                        </button>
                    </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                    {DATA_CARD_TYPES.map(({ focused, secondary }) => (
                        <ValueTypeOverviewComponent
                            key={focused}
                            focused={focused}
                            secondary={secondary}
                            compact
                            activeContextOptions={contextSelections[focused] ?? []}
                            onContextChange={opts =>
                                setContextSelections(prev => ({ ...prev, [focused]: opts }))
                            }
                        />
                    ))}
                </div>
            </div>
        </div>
    )
}
